import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL;

const options = {
    baseURL: baseURL || '/api/v2',
    withCredentials: true,
    timeout: 30000,
};

export const API = axios.create({
    ...options,
    headers: { 'Content-Type': 'application/json' },
});

export const APIRefresh = axios.create(options);

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
}> = [];

let csrfToken: string | null =
    document.querySelector('meta[name="_xsrf"]')?.getAttribute('content') ||
    null;
let requestId: string | null = null;

const processQueue = (error: Error | null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(null);
        }
    });
    failedQueue = [];
};

APIRefresh.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    config.headers['x-xsrf-token'] = csrfToken;
    config.headers['x-pre-xsrf-token'] = csrfToken;
    config.headers['x-request-id'] = requestId;
    return config;
});

APIRefresh.interceptors.response.use(
    (config) => {
        return config;
    },
    (error) => {
        processQueue(error as Error);

        if (error instanceof AxiosError) {
            csrfToken =
                error.config?.headers['x-csrf-token'] ||
                error.config?.headers['x-xsrf-token'] ||
                error.config?.headers['x-pre-xsrf-token'];
            requestId = error.config?.headers['x-request-id'];
        }

        if (
            error instanceof AxiosError &&
            error.code === 'ERR_BAD_REQUEST' &&
            error.response?.data.errorCode === 'ACCESS_UNAUTHORIZED'
        ) {
            return Promise.reject(error.response?.data);
        }
        return Promise.reject(error);
    },
);

API.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        if (csrfToken) {
            config.headers['X-XSRF-TOKEN'] = csrfToken;
            config.headers['X-PRE-XSRF-TOKEN'] = csrfToken;
            config.headers['X-CSRF-TOKEN'] = csrfToken;
        }
        if (requestId) {
            config.headers['x-request-id'] = requestId;
        }

        return config;
    },
    (error) => {
        csrfToken =
            error.response.headers['x-pre-xsrf-token'] ||
            error.response.headers['x-xsrf-token'];
        requestId = error.response.headers['x-request-id'];
        return Promise.reject(error);
    },
);

API.interceptors.response.use(
    (response) => {
        const newCsrfToken =
            response.headers['x-xsrf-token'] ||
            response.headers['x-csrf-token'] ||
            response.headers['x-pre-xsrf-token'];
        const newRequestId = response.headers['x-request-id'];
        if (newRequestId) {
            requestId = newRequestId;
        }
        if (newCsrfToken) {
            csrfToken = newCsrfToken;

            const metaTag = document.querySelector('meta[name="_xsrf"]');
            if (metaTag) {
                metaTag.setAttribute('content', csrfToken as string);
            }
        }

        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
            _retry?: boolean;
        };

        csrfToken =
            error.response?.headers['x-csrf-token'] ||
            error.response?.headers['x-pre-xsrf-token'];
        requestId = error.response?.headers['x-request-id'];

        const { data, status } = error.response || {};

        if (
            (status === 401 &&
                (data as { message: string }).message === 'Unauthorized' &&
                !originalRequest._retry) ||
            (data as { errorCode: string })?.errorCode ===
                'CSRF_TOKEN_IS_INVALID'
        ) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(() => {
                        return API(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshData = await APIRefresh.get('/auth/refresh');

                csrfToken =
                    refreshData.headers['x-csrf-token'] ||
                    refreshData.headers['x-xsrf-token'] ||
                    refreshData.headers['x-pre-xsrf-token'];
                requestId = refreshData.headers['x-request-id'];
                processQueue(null);
                return API(originalRequest);
            } catch (refreshError) {
                if (
                    refreshError instanceof AxiosError &&
                    import.meta.env.MODE === 'development'
                ) {
                    console.error(refreshError.message || '');
                }
            } finally {
                isRefreshing = false;
            }
        }

        if (
            (error.code === 'ECONNABORTED' && error.message.includes('time')) ||
            error.code === 'ERR_BAD_RESPONSE'
        ) {
            return Promise.reject(error);
        }

        const errorMessage = data || error.message || { message: data };
        return Promise.reject(errorMessage);
    },
);
export default API;
